import React from "react";
import {
    Box,
    Container,
    Heading,
    Text,
    VStack,
    HStack,
    SimpleGrid,
    Button,
    Icon,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { FaHeart, FaShieldAlt, FaLightbulb, FaGraduationCap } from "react-icons/fa";
import PublicNavbar from "../Landing/PublicNavbar";
import PublicFooter from "../Landing/PublicFooter";
import {
    DoodleUnderline,
    DoodleSparkle,
    WashiTape,
    DoodlePin,
    DoodlePaperclip,
} from "../../CustomUi/DoodleIcons";
import { APP_REGISTER_URL } from "../../../services/constants";

import { usePageTitle } from "../../../hooks/usePageTitle";

const AboutPage: React.FC = () => {
    usePageTitle(
        "About Us & Mission",
        "Built in Lagos, Nigeria. Result exists to eliminate end-of-term chaos and empower school owners, teachers, and parents."
    );
    const navigate = useNavigate();

    return (
        <Box minH="100vh" bg="#FAF8F2">
            <PublicNavbar />

            {/* Hero */}
            <Box py={[16, 24]} bg="brand.900" color="white" position="relative" overflow="hidden">
                <Container maxW="5xl">
                    <VStack gap={5} textAlign="center">
                        <HStack gap={2} justify="center">
                            <DoodleSparkle size={20} color="#FBBF24" />
                            <Box
                                px={3.5}
                                py={1}
                                bg="whiteAlpha.150"
                                borderRadius="full"
                                border="1px solid"
                                borderColor="whiteAlpha.200"
                            >
                                <Text fontSize="xs" fontWeight="bold" letterSpacing="widest" textTransform="uppercase" color="gold.300">
                                    Our Story
                                </Text>
                            </Box>
                            <DoodleSparkle size={20} color="#FBBF24" />
                        </HStack>

                        <Heading as="h1" fontSize={["3xl", "4xl", "5xl", "6xl"]} fontWeight="extrabold" lineHeight="1.1">
                            Built here.{" "}
                            <Box as="span" position="relative" display="inline-block" color="gold.400">
                                For here.
                                <DoodleUnderline position="absolute" bottom="-8px" left="0" color="#F59E0B" height="14px" />
                            </Box>
                        </Heading>
                        <Text fontSize={["md", "lg", "xl"]} opacity={0.8} maxW="2xl" lineHeight="1.8">
                            Result isn&apos;t an overseas product awkwardly adapted for Africa. It was designed from scratch in Lagos for the way Nigerian and African schools actually run.
                        </Text>
                    </VStack>
                </Container>
            </Box>

            {/* Story / Problem section styled as a notebook ledger */}
            <Box py={[16, 24]} className="notebook-dots">
                <Container maxW="4xl">
                    <Box
                        position="relative"
                        bg="white"
                        borderRadius="2xl"
                        border="3px solid #0A3D2E"
                        boxShadow="6px 8px 0px #0A3D2E"
                        p={[6, 10]}
                    >
                        <WashiTape angle={-3} top="-14px" left="36px" tapeColor="#FEF08A" label="The Mission ✏️" />
                        <DoodlePaperclip color="#10B981" size={36} position="absolute" top="-16px" right="40px" />

                        <VStack gap={6} align="start" pt={3}>
                            <Heading as="h2" fontSize={["2xl", "3xl"]} fontWeight="extrabold" color="gray.900">
                                End-of-term shouldn&apos;t take a week of stress
                            </Heading>
                            <Text fontSize="md" color="gray.600" lineHeight="1.9">
                                Every term, school administrators and teachers across Nigeria spend days doing exhausting manual work: compiling handwritten mark sheets, calculating continuous assessments on unstable Excel files, printing report cards one by one, and chasing bank teller slips.
                            </Text>
                            <Text fontSize="md" color="gray.600" lineHeight="1.9">
                                Result was built to eliminate that chaos. One clean platform that processes continuous assessments, generates verified report cards, collects school fees through dedicated virtual accounts, and communicates with parents in real time.
                            </Text>

                            <Box
                                bg="#FEF9C3"
                                border="2px dashed #0A3D2E"
                                borderRadius="xl"
                                p={5}
                                w="full"
                                position="relative"
                            >
                                <HStack gap={3} align="start">
                                    <Icon as={FaGraduationCap} color="brand.800" fontSize="2xl" mt={0.5} />
                                    <VStack align="start" gap={1}>
                                        <Text fontWeight="bold" color="#713F12" fontSize="sm">
                                            Our North Star:
                                        </Text>
                                        <Text className="font-handwriting" fontSize="xl" color="#713F12" fontWeight="bold" lineHeight="1.4">
                                            Give school leaders and educators their time back so they can focus on what matters most — nurturing the next generation of African leaders.
                                        </Text>
                                    </VStack>
                                </HStack>
                            </Box>
                        </VStack>
                    </Box>
                </Container>
            </Box>

            {/* Core Values - 3 Pinned Index Cards */}
            <Box py={[16, 20]} bg="#F4F1EA" borderTop="2px solid #0A3D2E" borderBottom="2px solid #0A3D2E">
                <Container maxW="7xl">
                    <VStack gap={[10, 14]}>
                        <VStack gap={3} textAlign="center">
                            <Text fontSize="xs" fontWeight="bold" letterSpacing="widest" textTransform="uppercase" color="brand.800">
                                Core Values
                            </Text>
                            <Heading as="h2" fontSize={["2xl", "3xl", "4xl"]} fontWeight="extrabold" color="gray.900">
                                Three things we don&apos;t compromise on
                            </Heading>
                        </VStack>

                        <SimpleGrid columns={[1, 3]} gap={8} w="full">
                            {[
                                {
                                    num: "01",
                                    icon: FaLightbulb,
                                    title: "Built for local context",
                                    noteColor: "#FEF9C3",
                                    pinColor: "#E11D48",
                                    rotation: -1.2,
                                    body: "Nigerian schools have specific academic calendars, local assessment formats, and parents who are mobile-first. We build specifically for that — not a generic overseas template.",
                                },
                                {
                                    num: "02",
                                    icon: FaHeart,
                                    title: "Zero fluff, zero bloat",
                                    noteColor: "#FFE4E6",
                                    pinColor: "#059669",
                                    rotation: 1,
                                    body: "Every tool in Result was requested by real teachers and bursars. We don't build bloated features to look complex; we build tools that save 10+ hours every week.",
                                },
                                {
                                    num: "03",
                                    icon: FaShieldAlt,
                                    title: "Institutional trust & security",
                                    noteColor: "#DCFCE7",
                                    pinColor: "#2563EB",
                                    rotation: -0.8,
                                    body: "Schools trust us with their student records and finances. We treat both with bank-grade encryption, complete audit trails, and 99.9% uptime reliability.",
                                },
                            ].map((val) => (
                                <Box
                                    key={val.num}
                                    position="relative"
                                    bg={val.noteColor}
                                    p={7}
                                    borderRadius="2xl"
                                    border="2.5px solid #0A3D2E"
                                    boxShadow="4px 5px 0px #0A3D2E"
                                    style={{ transform: `rotate(${val.rotation}deg)` }}
                                    transition="all 0.2s ease"
                                    _hover={{ transform: "rotate(0deg) scale(1.02)", boxShadow: "6px 7px 0px #0A3D2E" }}
                                >
                                    <Box position="absolute" top="-14px" left="50%" transform="translateX(-50%)" zIndex={2}>
                                        <DoodlePin color={val.pinColor} size={26} />
                                    </Box>

                                    <VStack align="start" gap={4} pt={2}>
                                        <HStack justify="space-between" w="full">
                                            <Box p={2.5} bg="white" borderRadius="xl" border="1.5px solid #0A3D2E">
                                                <Icon as={val.icon} color="brand.800" fontSize="lg" />
                                            </Box>
                                            <Text fontSize="sm" fontWeight="extrabold" color="brand.900" opacity={0.5}>
                                                {val.num}
                                            </Text>
                                        </HStack>
                                        <Heading as="h3" fontSize="lg" fontWeight="extrabold" color="gray.900">
                                            {val.title}
                                        </Heading>
                                        <Text fontSize="sm" color="gray.700" lineHeight="1.7">
                                            {val.body}
                                        </Text>
                                    </VStack>
                                </Box>
                            ))}
                        </SimpleGrid>
                    </VStack>
                </Container>
            </Box>

            {/* Bottom CTA banner */}
            <Box py={[16, 20]} bg="brand.900" color="white">
                <Container maxW="3xl">
                    <VStack gap={6} textAlign="center">
                        <Heading as="h2" fontSize={["2xl", "3xl", "4xl"]} fontWeight="extrabold">
                            Join hundreds of forward-thinking schools
                        </Heading>
                        <Text opacity={0.8} fontSize="md" maxW="lg">
                            Get started free or chat with our friendly team in Lagos.
                        </Text>
                        <HStack gap={4} flexWrap="wrap" justify="center" pt={2}>
                            <Button
                                size="lg"
                                bg="gold.400"
                                color="#1A1208"
                                fontWeight="bold"
                                px={8}
                                borderRadius="xl"
                                border="2px solid #1A1208"
                                boxShadow="3px 3px 0px #1A1208"
                                transition="all 0.15s ease"
                                _hover={{ bg: "gold.300", transform: "translate(-2px, -2px)", boxShadow: "5px 5px 0px #1A1208" }}
                                onClick={() => (window.location.href = APP_REGISTER_URL)}
                            >
                                Get Started Free
                            </Button>
                            <Button
                                size="lg"
                                variant="outline"
                                borderColor="whiteAlpha.400"
                                color="white"
                                px={8}
                                fontWeight="semibold"
                                borderRadius="xl"
                                transition="all 0.15s ease"
                                _hover={{ bg: "whiteAlpha.200", transform: "translateY(-2px)" }}
                                onClick={() => navigate("/contact")}
                            >
                                Get in Touch
                            </Button>
                        </HStack>
                    </VStack>
                </Container>
            </Box>

            <PublicFooter />
        </Box>
    );
};

export default AboutPage;
